import { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money } from "@/data/constants";
import { useBranding } from "@/context/BrandingContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Smartphone, ShieldCheck, Loader2, CheckCircle2, Tag } from "lucide-react";
import { toast } from "sonner";

const POLL_INTERVAL = 3000;
const MAX_POLLS = 20; // ~60s

export const PaywallModal = ({ open, onOpenChange, listing, onUnlocked, payPath }) => {
  const { free_unlock } = useBranding();
  const [phone, setPhone] = useState("");
  const [stage, setStage] = useState("form"); // form | waiting | timeout | done
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [receipt, setReceipt] = useState("");
  const cancelled = useRef(false);
  const checkoutId = useRef(null);

  const basePath = payPath || `/listings/${listing.id}/pay`;
  const stkPath = basePath.replace("/pay", "/stk");
  const freePath = basePath.replace("/pay", "/free-unlock");

  const reset = () => {
    cancelled.current = true;
    checkoutId.current = null;
    setPhone("");
    setStage("form");
    setError("");
    setMessage("");
    setReceipt("");
  };

  const finishUnlock = (contact, ref, mpesaReceipt) => {
    setStage("done");
    setReceipt(mpesaReceipt || "");
    toast.success("Payment confirmed — contact unlocked!");
    const unlocked = JSON.parse(localStorage.getItem("rento_unlocked") || "{}");
    unlocked[listing.id] = ref;
    localStorage.setItem("rento_unlocked", JSON.stringify(unlocked));
    setTimeout(() => {
      onUnlocked(contact, ref);
      onOpenChange(false);
      reset();
    }, 900);
  };

  const pollStatus = async (cid, attempt = 0) => {
    if (cancelled.current || checkoutId.current !== cid) return;
    if (attempt >= MAX_POLLS) {
      setStage("timeout");
      setMessage("We haven't received a confirmation yet. If you completed the payment, tap Check again.");
      return;
    }
    try {
      // Use the query API as a fallback nudge every few polls.
      const path = attempt > 0 && attempt % 3 === 0
        ? `/mpesa/reconcile/${cid}`
        : `/mpesa/status/${cid}`;
      const { data } = attempt > 0 && attempt % 3 === 0
        ? await api.post(path)
        : await api.get(path);
      if (cancelled.current || checkoutId.current !== cid) return;
      if (data.status === "paid") {
        finishUnlock(data.contact, data.payment_ref, data.receipt);
        return;
      }
      if (data.status === "failed") {
        setStage("form");
        setError("Payment was cancelled or failed. Please try again.");
        return;
      }
    } catch (e) {
      // transient — keep polling
    }
    setTimeout(() => pollStatus(cid, attempt + 1), POLL_INTERVAL);
  };

  const handleFreeUnlock = async () => {
    setError("");
    setStage("waiting");
    setMessage("Unlocking contact…");
    try {
      const { data } = await api.post(freePath);
      finishUnlock(data.contact, data.payment_ref, "");
    } catch (e) {
      setStage("form");
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const startPayment = async () => {    setError("");
    if (!/^(?:254|0)?7\d{8}$/.test(phone.replace(/\s/g, ""))) {
      setError("Enter a valid Safaricom number e.g. 0712345678");
      return;
    }
    cancelled.current = false;
    setStage("waiting");
    setMessage("Sending an M-Pesa prompt to your phone…");
    try {
      const { data } = await api.post(stkPath, { phone });
      checkoutId.current = data.checkout_request_id;
      setMessage(data.customer_message || "Check your phone and enter your M-Pesa PIN.");
      pollStatus(data.checkout_request_id, 0);
    } catch (e) {
      setStage("form");
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const checkAgain = () => {
    if (!checkoutId.current) return;
    setStage("waiting");
    setMessage("Checking payment status…");
    cancelled.current = false;
    pollStatus(checkoutId.current, MAX_POLLS - 3);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent data-testid="paywall-modal" className="max-w-md rounded-2xl border-border/60 p-0 overflow-hidden">
        <div className="bg-[#2C3830] p-6 text-white">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-white">
              Unlock contact details
            </DialogTitle>
            <DialogDescription className="text-white/70">
              {free_unlock
                ? "Reveal the owner / agent's phone and email — free while we finish setting up payments."
                : "Pay the platform access fee via M-Pesa to reveal the owner / agent's phone and email."}
            </DialogDescription>
          </DialogHeader>
          {listing.offer_active && (
            <div data-testid="offer-banner" className="mt-4 flex items-center gap-2 rounded-xl bg-[#25D366]/20 px-3 py-2 text-sm text-white">
              <Tag className="h-4 w-4" /> {listing.offer_label || "Limited-time offer"}
            </div>
          )}
          <div className="mt-4 flex items-end justify-between rounded-xl bg-white/10 p-4">
            <span className="text-sm text-white/70">Access fee</span>
            <div className="text-right">
              {listing.offer_active && listing.base_fee > listing.unlock_fee && (
                <span className="mr-2 text-sm text-white/50 line-through">{money(listing.base_fee, listing.currency)}</span>
              )}
              <span data-testid="paywall-fee" className="font-display text-3xl font-semibold">
                {free_unlock ? "Free" : money(listing.unlock_fee, listing.currency)}
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-4 p-6">
          {stage === "done" ? (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <CheckCircle2 className="h-14 w-14 text-[#25D366]" />
              <p className="font-display text-lg text-[#2C3830]">Payment successful</p>
              {receipt ? (
                <p data-testid="paywall-receipt" className="text-sm text-[#6B7280]">
                  M-Pesa receipt: <span className="font-mono font-semibold text-[#2C3830]">{receipt}</span>
                </p>
              ) : (
                <p className="text-sm text-[#6B7280]">Revealing contact details…</p>
              )}
            </div>
          ) : stage === "waiting" ? (
            <div className="flex flex-col items-center gap-3 py-8 text-center" data-testid="paywall-waiting">
              <Loader2 className="h-12 w-12 animate-spin text-[#25D366]" />
              <p className="font-display text-lg text-[#2C3830]">Waiting for M-Pesa…</p>
              <p className="text-sm text-[#6B7280]">{message}</p>
            </div>
          ) : stage === "timeout" ? (
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <p className="text-sm text-[#6B7280]">{message}</p>
              <Button
                data-testid="paywall-check-again-btn"
                onClick={checkAgain}
                className="w-full rounded-full bg-[#25D366] py-6 text-base font-semibold text-white hover:bg-[#1fb85a]"
              >
                Check again
              </Button>
              <button
                data-testid="paywall-retry-btn"
                onClick={reset}
                className="text-sm text-[#6B7280] underline"
              >
                Start over
              </button>
            </div>
          ) : free_unlock ? (
            <div className="space-y-4">
              {error && (
                <p data-testid="paywall-error" className="text-sm text-destructive">{error}</p>
              )}
              <Button
                data-testid="free-unlock-btn"
                onClick={handleFreeUnlock}
                className="w-full rounded-full bg-[#25D366] py-6 text-base font-semibold text-white hover:bg-[#1fb85a]"
              >
                Reveal contact — Free
              </Button>
              <div className="flex items-center justify-center gap-1.5 text-xs text-[#6B7280]">
                <ShieldCheck className="h-3.5 w-3.5" />
                Contacts are currently free to unlock.
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label className="label-caps text-[#6B7280]">M-Pesa Phone Number</Label>
                <div className="relative">
                  <Smartphone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6B7280]" />
                  <Input
                    data-testid="paywall-phone-input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="0712 345 678"
                    className="rounded-xl pl-9"
                  />
                </div>
                {error && (
                  <p data-testid="paywall-error" className="text-sm text-destructive">
                    {error}
                  </p>
                )}
              </div>

              <Button
                data-testid="paywall-pay-btn"
                onClick={startPayment}
                className="w-full rounded-full bg-[#25D366] py-6 text-base font-semibold text-white hover:bg-[#1fb85a]"
              >
                Pay {money(listing.unlock_fee, listing.currency)} with M-Pesa
              </Button>

              <div className="flex items-center justify-center gap-1.5 text-xs text-[#6B7280]">
                <ShieldCheck className="h-3.5 w-3.5" />
                Secure M-Pesa STK Push — enter your PIN on your phone.
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
